import {
  debounce,
  closeDrawer,
  trapFocus,
  removeTrapFocus,
  lockScroll,
  unlockScroll,
  minWidthQuery,
  getObserver,
  setInteractionDisabled,
  getStableViewportHeight,
} from '@theme/theme-global';
import { ScrollManager } from '@theme/event-manager';
import motion from '@lib/motion';

class FilterBar extends HTMLElement {
  constructor() {
    super();
    this.onChipClick = this.onChipClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onFilterInput(event);
    }, 800);

    const facetForm = this.querySelector('[data-facet-form]') || this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    if (this.querySelector('.sort-listbox')) {
      this.onSortKeyDown = this.onSortKeyDown.bind(this);
      this.addEventListener('keydown', this.onSortKeyDown);
    }
  }

  onSortKeyDown(event) {
    if (event.key !== 'Enter') return;

    const item = event.target.closest('.sort-listbox-item');
    if (!item) return;

    const input = item.querySelector('input[type="radio"]');
    if (!input) return;

    event.preventDefault();
    input.click();
  }

  static bindHistory() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FilterBar.searchParamsInitial;
      if (searchParams === FilterBar.searchParamsPrev) return;
      FilterBar.refreshCollection(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static setChipsDisabled(disable = true) {
    document.querySelectorAll('[data-facet-remove]').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static parseHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  static refreshCollection(searchParams, event, shouldWriteUrl = true) {
    FilterBar.searchParamsPrev = searchParams;
    const sections = FilterBar.listSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FilterBar.filterData.some(filterDataUrl)
        ? FilterBar.applyCachedHtml(filterDataUrl, event)
        : FilterBar.fetchSectionHtml(url, event);
    });

    if (shouldWriteUrl) FilterBar.writeUrlQuery(searchParams);
  }

  static fetchSectionHtml(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        FilterBar.filterData = [...FilterBar.filterData, { html: responseText, url }];
        FilterBar.applySectionHtml(responseText, event);
      });
  }

  static applyCachedHtml(filterDataUrl, event) {
    const html = FilterBar.filterData.find(filterDataUrl).html;
    FilterBar.applySectionHtml(html, event);
  }

  static applySectionHtml(html, event) {
    const parsedHTML = FilterBar.parseHtml(html);
    FilterBar.swapFilterBlocks(parsedHTML, event);
    FilterBar.swapMobileForm(parsedHTML);
    FilterBar.swapProductGrid(parsedHTML);
    FilterBar.swapProductCount(parsedHTML);
    FilterBar.swapActiveChips(parsedHTML);
    FilterBar.swapSorting(parsedHTML);
    syncFacetsKeyboardAccess();
  }

  static swapProductGrid(parsedHTML) {
    const newProductGrid = parsedHTML.querySelector('[data-product-grid]');
    const productGrid = document.querySelector('[data-product-grid]');

    if (newProductGrid && productGrid) {
      productGrid.innerHTML = newProductGrid.innerHTML;
    }
  }

  static swapProductCount(parsedHTML) {
    const countElement = parsedHTML.getElementById('ProductCount');

    if (countElement) {
      const count = countElement.innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');

      if (container) {
        container.innerHTML = count;
        container.classList.remove('loading');
      }
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove('loading');
      }
    }

    document.querySelectorAll('filter-bar .loading-spinner').forEach((spinner) => {
      spinner.classList.add('hidden');
    });
  }

  static getFacetFocusTarget(event) {
    const target = event?.target;
    if (!target || !target.tagName) return null;

    const tag = target.tagName;
    if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return null;

    return {
      id: target.id || null,
      name: target.getAttribute('name'),
      value: target.value,
      type: target.getAttribute('type'),
    };
  }

  static restoreFacetFocus(container, focusTarget) {
    if (!container) return;

    let nextFocus = null;

    if (focusTarget?.id) {
      const byId = document.getElementById(focusTarget.id);
      if (byId && container.contains(byId)) nextFocus = byId;
    }

    if (!nextFocus && focusTarget?.name != null) {
      const escapedName =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(focusTarget.name)
          : focusTarget.name.replace(/"/g, '\\"');
      const candidates = container.querySelectorAll(
        `input[name="${escapedName}"], select[name="${escapedName}"], textarea[name="${escapedName}"]`,
      );
      nextFocus =
        Array.from(candidates).find((element) => element.value === focusTarget.value) || candidates[0] || null;
    }

    // Fallback when the activated value was removed (e.g. count became 0).
    if (!nextFocus) {
      nextFocus = container.querySelector('summary');
    }

    nextFocus?.focus({ preventScroll: true });
  }

  static swapFilterBlocks(parsedHTML, event) {
    const focusTarget = FilterBar.getFacetFocusTarget(event);
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('[data-facet-filter]');
    const facetDetailsElementsFromDom = document.querySelectorAll('[data-facet-filter]');

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const activeFilter = event?.target?.closest('[data-facet-filter]');
    const matchesId = (element) => (activeFilter ? element.id === activeFilter.id : false);

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const activeFacetFromFetch = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        FilterBar.swapFilterContent(elementToRender, currentElement);
        return;
      }

      if (index > 0) {
        const previousElement = facetsToRender[index - 1];
        const previousInDom = document.getElementById(previousElement.id);
        if (previousInDom) {
          previousInDom.after(elementToRender);
          return;
        }
      }

      const parentFromFetch = elementToRender.parentElement;
      const parentInDom = parentFromFetch?.id
        ? document.getElementById(parentFromFetch.id)
        : document.querySelector('[data-facet-form="filters"]:not([data-facet-context="mobile"])');

      if (!parentInDom) return;

      const firstFilter = parentInDom.querySelector('[data-facet-filter]');
      if (firstFilter) {
        firstFilter.before(elementToRender);
      } else {
        // Form was emptied (S&D hide / clear). Re-seed filters into the empty parent.
        parentInDom.append(elementToRender);
      }
    });

    if (activeFacetFromFetch && activeFilter) {
      FilterBar.swapFilterContent(activeFacetFromFetch, activeFilter);
      FilterBar.restoreFacetFocus(activeFilter, focusTarget);
    }
  }

  static swapFilterContent(source, target) {
    const sourceSummary = source.querySelector('summary');
    const targetSummary = target.querySelector('summary');

    if (sourceSummary && targetSummary) {
      const ariaLabel = sourceSummary.getAttribute('aria-label');
      if (ariaLabel) targetSummary.setAttribute('aria-label', ariaLabel);
    }

    const sourceContent = source.querySelector('.accordion-details-content');
    const targetContent = target.querySelector('.accordion-details-content');

    if (sourceContent && targetContent) {
      // Keep the same content node so accordion-details contentElement stays valid
      targetContent.innerHTML = sourceContent.innerHTML;
      return;
    }

    target.innerHTML = source.innerHTML;
  }

  static swapMobileForm(parsedHTML) {
    const newForm = parsedHTML.querySelector('[data-facet-form="filters"][data-facet-context="mobile"]');
    const currentForm = document.querySelector('[data-facet-form="filters"][data-facet-context="mobile"]');
    if (!newForm || !currentForm) return;

    const tabComponent = currentForm.closest('tab-component');
    const activeIndex = currentForm.querySelector('[role="tab"].active')?.dataset.index;

    // Full replace so S&D hide-empty-values can add/remove filters and values.
    currentForm.innerHTML = newForm.innerHTML;

    if (tabComponent && typeof tabComponent.refresh === 'function') {
      tabComponent.refresh(activeIndex);
    }
  }

  static swapActiveChips(parsedHTML) {
    const activeFacetsElement =
      parsedHTML.querySelector('[data-facet-active]') || parsedHTML.querySelector('#FacetFiltersActive');
    if (!activeFacetsElement) return;

    document.querySelectorAll('[data-facet-active]').forEach((targetElement) => {
      targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FilterBar.syncClearAllChip();
    FilterBar.setChipsDisabled(false);
  }

  static syncClearAllChip() {
    document.querySelectorAll('[data-facet-active]').forEach((container) => {
      const appliedCount = container.querySelectorAll('.facet-filter-remove').length;
      const clearChip = container.querySelector('.facet-filter-clear')?.closest('filter-chip');
      if (!clearChip) return;
      clearChip.classList.toggle('d-none', appliedCount < 2);
    });
  }

  static swapSorting(parsedHTML) {
    const newSorting = parsedHTML.querySelector('[data-facet-sorting]');
    const currentSorting = document.querySelector('[data-facet-sorting]');
    if (!newSorting || !currentSorting) return;

    currentSorting.innerHTML = newSorting.innerHTML;
  }

  static writeUrlQuery(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static listSections() {
    const productGrid = document.querySelector('[data-product-grid]');
    if (!productGrid) return [];

    return [{ section: productGrid.dataset.productGrid }];
  }

  buildQueryString(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  collectFilterForms(sourceForm) {
    const forms = [sourceForm];
    const sourceType = sourceForm.dataset.facetForm;

    if (sourceType === 'filters') {
      // Desktop + mobile each render a sort form; only include one to avoid
      // duplicated sort_by=…&sort_by=… in the URL.
      const sortForm = document.querySelector('[data-facet-form="sort"]');
      if (sortForm) forms.push(sortForm);
      return forms;
    }

    if (sourceType === 'sort') {
      const desktopFilters = document.querySelector(
        '[data-facet-form="filters"]:not([data-facet-context="mobile"])',
      );
      const mobileFilters = document.querySelector(
        '[data-facet-form="filters"][data-facet-context="mobile"]',
      );
      const filtersForm = desktopFilters || mobileFilters;
      if (filtersForm) forms.push(filtersForm);
    }

    return forms;
  }

  submitFilters(searchParams, event) {
    FilterBar.refreshCollection(searchParams, event);
  }

  onFilterInput(event) {
    event.preventDefault();

    const sourceForm = event.target.closest('[data-facet-form]');
    if (!sourceForm) return;

    const searchParams = this.collectFilterForms(sourceForm)
      .map((form) => this.buildQueryString(form))
      .filter(Boolean)
      .join('&');

    this.submitFilters(searchParams, event);
  }

  onChipClick(event) {
    event.preventDefault();
    FilterBar.setChipsDisabled();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FilterBar.refreshCollection(url);
  }
}

FilterBar.filterData = [];
FilterBar.searchParamsInitial = window.location.search.slice(1);
FilterBar.searchParamsPrev = window.location.search.slice(1);
customElements.define('filter-bar', FilterBar);
FilterBar.bindHistory();
FilterBar.syncClearAllChip();


class PriceSlider extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('input[type="range"][data-min-range]');
    this.rangeMax = this.querySelector('input[type="range"][data-max-range]');
    this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
    this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

    this.inputMin.addEventListener('focus', this.inputMin.select);
    this.inputMax.addEventListener('focus', this.inputMax.select);
    this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
    this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

    this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
    this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
    this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
    this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
  }

  onInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(
      Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1),
      event.target.min,
    );
    this.rangeMin.value = event.target.value;
    this.rangeMin.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max)) * 100}%`,
    );
  }

  onInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(
      Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1),
      event.target.max,
    );
    this.rangeMax.value = event.target.value;
    this.rangeMax.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max)) * 100}%`,
    );
  }

  onRangeMinChange(event) {
    event.stopPropagation();
    this.inputMin.value = event.target.value;
    this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMaxChange(event) {
    event.stopPropagation();
    this.inputMax.value = event.target.value;
    this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMinInput(event) {
    event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`,
    );
    this.inputMin.value = event.target.value;
  }

  onRangeMaxInput(event) {
    event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`,
    );
    this.inputMax.value = event.target.value;
  }
}
customElements.define('price-slider', PriceSlider);

class FilterChip extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.clearChip.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.clearChip(event);
    });
  }

  clearChip(event) {
    event.preventDefault();
    const form = this.closest('filter-bar') || document.querySelector('filter-bar');
    form.onChipClick(event);
  }
}

customElements.define('filter-chip', FilterChip);

class FilterToggle extends HTMLButtonElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
    this.targetFacet = this.dataset.targetFacet;
    this.actionType = this.dataset.action;
    this._handleKeyup = (evt) => {
      if (evt.code === 'Escape') this.escapeClose();
    };
    this._onEditorUnlock = this.onEditorUnlock.bind(this);
  }

  connectedCallback() {
    if (this.actionType === 'drawer' || this.actionType === 'popover') {
      this.addEventListener('click', this.drawerActions.bind(this));
      this.close();

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:unload', this._onEditorUnlock);
        document.addEventListener('shopify:section:load', this._onEditorUnlock);
      }
    }

    if (this.actionType === 'sidebar') {
      this.addEventListener('click', this.sidebarToggle.bind(this));
    }

    if (this.actionType === 'MobileFacetFiltersForm') {
      this.addEventListener('click', this.mobileDrawerActions.bind(this));
      this.close();
    }
  }

  disconnectedCallback() {
    if (this.actionType === 'drawer' || this.actionType === 'popover') {
      this.removeEventListener('click', this.drawerActions.bind(this));
      unlockScroll();

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:section:unload', this._onEditorUnlock);
        document.removeEventListener('shopify:section:load', this._onEditorUnlock);
      }
    }
    if (this.actionType === 'sidebar') {
      this.removeEventListener('click', this.sidebarToggle.bind(this));
    }
    if (this.actionType === 'MobileFacetFiltersForm') {
      this.removeEventListener('click', this.mobileDrawerActions.bind(this));
    }
  }

  onEditorUnlock(event) {
    const section = event.target?.closest?.('.shopify-section') ?? event.target;
    if (!section || !this.section) return;
    if (section !== this.section && !section.contains(this) && !this.section.contains(section)) return;
    unlockScroll();
  }

  drawerActions(event) {
    const facetsDrawer = document.querySelector('facets-drawer') || document.querySelector('facets-popover');

    if (!facetsDrawer) return;
    facetsDrawer.activeTrigger = this;

    if (this.actionType === 'drawer') {
      this.targetFacet = this.dataset.targetFacet;
      if (!this.targetFacet) return;
      const facetDetails = facetsDrawer.querySelector(`#${CSS.escape(this.targetFacet)}`);
      if (facetDetails) {
        facetDetails.onSummaryClick(event);
      }
      facetsDrawer.setAttribute('open', '');
      facetsDrawer.setAttribute('active', '');
      syncFacetsKeyboardAccess();
      facetsDrawer.addEventListener(
        'transitionend',
        () => {
          trapFocus(facetsDrawer, facetsDrawer);
        },
        { once: true },
      );
      lockScroll();
    }
    if (this.actionType === 'popover') {
      facetsDrawer.setAttribute('open', '');
      facetsDrawer.setAttribute('active', '');
      syncFacetsKeyboardAccess();
      facetsDrawer.addEventListener(
        'transitionend',
        () => {
          trapFocus(facetsDrawer, facetsDrawer);
        },
        { once: true },
      );
      lockScroll();
    }
  }

  mobileDrawerActions(event) {
    const facetsDrawer = document.querySelector('facets-mobile-drawer');
    if (!facetsDrawer) return;
    facetsDrawer.activeTrigger = this;
    facetsDrawer.setAttribute('open', '');
    facetsDrawer.setAttribute('active', '');
    syncFacetsKeyboardAccess();
    facetsDrawer.addEventListener(
      'transitionend',
      () => {
        trapFocus(facetsDrawer, facetsDrawer);
      },
      { once: true },
    );
    lockScroll();
  }

  getFacetsDrawerSelector() {
    if (this.actionType === 'MobileFacetFiltersForm') return 'facets-mobile-drawer';
    if (this.actionType === 'popover') return 'facets-popover';
    return 'facets-drawer';
  }

  close() {
    const facetsDrawers = document.querySelectorAll(this.getFacetsDrawerSelector());
    facetsDrawers.forEach((facetsDrawer) => {
      facetsDrawer.addEventListener('keyup', this._handleKeyup);
      const closeTriggers = facetsDrawer.querySelectorAll('[is="close-drawer"]');
      closeTriggers.forEach((closeTrigger) => {
        closeTrigger.addEventListener('click', (e) => {
          closeDrawer(e.target);
          const activeTrigger = facetsDrawer.activeTrigger;
          if (activeTrigger) {
            removeTrapFocus(activeTrigger);
          }
          if (this.actionType === 'drawer') {
            debounce(() => {
              facetsDrawer.querySelectorAll('details').forEach((detail) => {
                if (detail.hasAttribute('open')) {
                  detail.close();
                }
              });
            }, 500)();
          }
        });
      });
    });
  }

  escapeClose() {
    const facetsDrawers = document.querySelectorAll(this.getFacetsDrawerSelector());
    facetsDrawers.forEach((facetsDrawer) => {
      if (!facetsDrawer.hasAttribute('open')) return;

      closeDrawer(facetsDrawer);
      const trigger = facetsDrawer.activeTrigger;
      if (trigger) {
        removeTrapFocus(trigger);
      }
      if (this.actionType === 'drawer') {
        debounce(() => {
          facetsDrawer.querySelectorAll('details').forEach((detail) => {
            if (detail.hasAttribute('open')) {
              detail.close();
            }
          });
        }, 500)();
      }
    });
  }

  sidebarToggle() {
    const itemsWrapper = this.section.querySelector('[data-items-wrapper]');
    if (!itemsWrapper) return;

    itemsWrapper.classList.toggle('open-filters');

    debounce(() => {
      const label = this.querySelector('[data-button-label]');
      if (!label || !window.facetsStrings) return;

      label.textContent = itemsWrapper.classList.contains('open-filters')
        ? window.facetsStrings.hideFilters
        : window.facetsStrings.showFilters;
    }, 300)();
  }
}
customElements.define('filter-toggle', FilterToggle, { extends: 'button' });

class FilterSidebar extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.shopify-section');
  }

  connectedCallback() {
    this.syncSidebarButtonLabel();
  }

  getItemsWrapper() {
    return this.section?.querySelector('[data-items-wrapper]');
  }

  getSidebarButtonLabel() {
    return this.section?.querySelector('[is="filter-toggle"][data-action="sidebar"] [data-button-label]');
  }

  syncSidebarButtonLabel() {
    const itemsWrapper = this.getItemsWrapper();
    const buttonLabel = this.getSidebarButtonLabel();
    if (!itemsWrapper || !buttonLabel || !window.facetsStrings) return;

    buttonLabel.textContent = itemsWrapper.classList.contains('open-filters')
      ? window.facetsStrings.hideFilters
      : window.facetsStrings.showFilters;
  }
}
customElements.define('filter-sidebar', FilterSidebar);

function closeFacetSurfaceIfOpen(drawer) {
  if (!drawer) return;
  if (!drawer.hasAttribute('open') && !drawer.hasAttribute('active')) return;

  const trigger = drawer.activeTrigger;
  closeDrawer(drawer);

  if (trigger) {
    removeTrapFocus(trigger);
  } else {
    removeTrapFocus();
  }
}

function setFacetSurfaceAccessibility(surface, shouldDisable) {
  setInteractionDisabled(surface, shouldDisable);
}

function isFacetSurfaceActive(surface) {
  return surface.hasAttribute('active');
}

function syncFacetsKeyboardAccess() {
  const isDesktop = facetsViewportQuery.matches;

  document.querySelectorAll('facets-drawer, facets-popover').forEach((surface) => {
    // Desktop-only UI: disable on mobile, or when closed on desktop.
    setFacetSurfaceAccessibility(surface, !isDesktop || !isFacetSurfaceActive(surface));
  });

  document.querySelectorAll('facets-mobile-drawer').forEach((surface) => {
    // Mobile-only UI: disable on desktop, or when closed on mobile.
    setFacetSurfaceAccessibility(surface, isDesktop || !isFacetSurfaceActive(surface));
  });
}

function onFacetsBreakpointChange(event) {
  if (event.matches) {
    document.querySelectorAll('facets-mobile-drawer').forEach(closeFacetSurfaceIfOpen);
  } else {
    document.querySelectorAll('facets-drawer, facets-popover').forEach(closeFacetSurfaceIfOpen);
  }

  syncFacetsKeyboardAccess();
}

const facetsViewportQuery = minWidthQuery(768);
facetsViewportQuery.addEventListener('change', onFacetsBreakpointChange);

document.addEventListener('drawer:close', (event) => {
  if (!event.target?.matches?.('facets-drawer, facets-popover, facets-mobile-drawer')) return;
  syncFacetsKeyboardAccess();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncFacetsKeyboardAccess, { once: true });
} else {
  syncFacetsKeyboardAccess();
}

/**
 * Tab focus in facet drawer/popover: keep the focused control visible in .drawer-body.
 * Undo the browser's focus scroll first, then scroll only if still clipped.
 */
(function initFacetDrawerFocusScroll() {
  const SURFACE = 'facets-drawer, facets-popover, facets-mobile-drawer';
  let tabPressed = false;
  let savedScrollTop = 0;
  let savedBody = null;

  function visualTarget(el) {
    if (el.labels?.length) return el.labels[0];
    if (el.id) {
      const escaped =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(el.id) : el.id;
      const label = document.querySelector(`label[for="${escaped}"]`);
      if (label) return label;
    }
    return el;
  }

  function isInView(el, body) {
    const er = el.getBoundingClientRect();
    const br = body.getBoundingClientRect();
    return er.top >= br.top && er.bottom <= br.bottom;
  }

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.code?.toUpperCase() !== 'TAB') return;
      const surface = event.target?.closest?.(SURFACE);
      if (!surface?.hasAttribute('active') && !surface?.hasAttribute('open')) return;
      const body = surface.querySelector('.drawer-body');
      if (!body) return;
      tabPressed = true;
      savedBody = body;
      savedScrollTop = body.scrollTop;
    },
    true,
  );

  document.addEventListener(
    'focusin',
    (event) => {
      if (!tabPressed) return;
      tabPressed = false;

      const el = event.target;
      const body = el?.closest?.('.drawer-body');
      if (!body || !body.closest(SURFACE)) return;

      // Undo browser focus-scroll so we only move when actually needed.
      const restoreTo = savedBody === body ? savedScrollTop : body.scrollTop;
      body.scrollTop = restoreTo;

      const target = visualTarget(el);
      if (isInView(target, body)) return;

      const er = target.getBoundingClientRect();
      const br = body.getBoundingClientRect();
      if (er.top < br.top) body.scrollTop += er.top - br.top;
      else if (er.bottom > br.bottom) body.scrollTop += er.bottom - br.bottom;
    },
    true,
  );
})();

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
    this.inViewObserver = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') === 'infinite') {
      this.initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
    if (this.inViewObserver) {
      this.inViewObserver.unobserve(this);
      this.inViewObserver = null;
    }
  }

  onClick(event) {
    event?.preventDefault();
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.swapProductGrid(parsedHTML);
        this.renderPagination(parsedHTML);
      });
  }

  findProductGrid() {
    return this.closest('[data-product-grid]') || document.querySelector('[data-product-grid]');
  }

  renderPagination(parsedHTML) {
    const productGridContainer = this.findProductGrid();
    if (!productGridContainer) return;

    const pagination = productGridContainer.querySelector('.pagination-wrapper');
    const sourceGrid = parsedHTML.querySelector(`[data-product-grid="${productGridContainer.dataset.productGrid}"]`);
    const source = sourceGrid?.querySelector('.pagination-wrapper') || parsedHTML.querySelector('.pagination-wrapper');

    if (source && pagination) {
      pagination.outerHTML = source.outerHTML;
      return;
    }

    if (!source && pagination) {
      pagination.remove();
    }
  }

  swapProductGrid(parsedHTML) {
    const productGridContainer = this.findProductGrid();
    if (!productGridContainer) return;

    const productList = productGridContainer.querySelector('[data-product-list="true"]');
    const sourceGrid = parsedHTML.querySelector(`[data-product-grid="${productGridContainer.dataset.productGrid}"]`);
    const source =
      sourceGrid?.querySelector('[data-product-list="true"]') || parsedHTML.querySelector('[data-product-list="true"]');

    if (source && productList) {
      source.querySelectorAll('[data-product-item="true"]').forEach((item) => {
        productList.appendChild(item);
      });
    }
  }

  initInfiniteScroll() {
    this.inViewObserver = getObserver({ rootMargin: '200px 0px 200px 0px', threshold: 0 }, false, false);
    this.inViewObserver.observe(this, (entry) => {
      if (entry.isIntersecting) this.onClick();
    });
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));

    const productGridContainer = this.findProductGrid();
    const sectionId = productGridContainer?.dataset.productGrid;
    if (sectionId) url.searchParams.set('section_id', sectionId);

    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}

customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });


class FilterStickyBar extends HTMLDivElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    if (!this.section) return;

    // Unique options so getObserver map doesn't reuse another once/callOnExit pair.
    this._observer = getObserver({ rootMargin: '0px', threshold: 0 }, false, true);
    this._observer.observe(this.section, this._onScroll);

    ScrollManager.subscribe(this._onScroll);
    this._onScroll();
  }

  disconnectedCallback() {
    ScrollManager.unsubscribe(this._onScroll);
    if (this._observer && this.section) {
      this._observer.unobserve(this.section);
    }
  }

  _onScroll() {
    if (!this.section) return;
    const { top, bottom } = this.section.getBoundingClientRect();
    const viewportHeight = getStableViewportHeight();
    // Hysteresis so iOS URL-bar height jitter near the center line doesn't toggle.
    const isHidden = this.classList.contains('is-hidden');
    const hideLine = viewportHeight * 0.5;
    const showLine = viewportHeight * 0.55;
    const line = isHidden ? showLine : hideLine;
    const isVisible = top < viewportHeight && bottom > line;
    this.classList.toggle('is-hidden', !isVisible);
  }
}
customElements.define('filter-sticky-bar', FilterStickyBar, { extends: 'div' });


class SortDetails extends HTMLDetailsElement {
  connectedCallback() {
    this.panel = this.querySelector('.facet-sort-wrapper');
    this.summaryEl = this.querySelector('summary');
    if (!this.panel || !this.summaryEl) return;

    this.open = false;
    this._isOpen = false;
    this.panel.hidden = true;
    this.summaryEl.addEventListener('click', this.onToggle);
    this.addEventListener('focusout', this.onFocusOut);
    this.onDocClick = (e) => !this.contains(e.target) && this.close();
  }

  disconnectedCallback() {
    this.summaryEl?.removeEventListener('click', this.onToggle);
    this.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('click', this.onDocClick);
  }

  onToggle = (e) => {
    e.preventDefault();
    this._isOpen ? this.close() : this.show();
  };

  onFocusOut = (e) => {
    const next = e.relatedTarget;
    requestAnimationFrame(() => {
      if (!this._isOpen) return;
      const active = document.activeElement;
      if (this.contains(active)) return;
      if (next && this.contains(next)) return;
      this.close();
    });
  };

  show() {
    this.animation?.cancel?.();
    this._isOpen = true;
    this.open = true;
    this.panel.hidden = false;
    document.addEventListener('click', this.onDocClick);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.animation = motion.animate(
      this.panel,
      { opacity: [0, 1], transform: ['scale(0.95)', 'scale(1)'] },
      { duration: reduce ? 0 : 0.2, easing: [0.22, 1, 0.36, 1] },
    );
  }

  close() {
    if (!this._isOpen) return;
    this.animation?.cancel?.();
    this._isOpen = false;
    document.removeEventListener('click', this.onDocClick);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.animation = motion.animate(
      this.panel,
      { opacity: [1, 0], transform: ['scale(1)', 'scale(0.95)'] },
      { duration: reduce ? 0 : 0.16, easing: [0.4, 0, 1, 1] },
    );
    this.animation.finished.then(() => {
      this.panel.hidden = true;
      this.panel.style.opacity = '';
      this.panel.style.transform = '';
      this.open = false;
    }).catch(() => { });
  }
}
customElements.define('sort-details', SortDetails, { extends: 'details' });


class CategoriesToggle extends HTMLButtonElement {
  constructor() {
    super();
    this._isOpen = false;
    this.onToggle = this.onToggle.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
  }

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.categoriesPanel =
      this.closest('[data-sticky-bar]')?.querySelector('[data-desktop-categories="true"]') ||
      this.section?.querySelector('[data-desktop-categories="true"]');

    if (!this.categoriesPanel) return;

    setInteractionDisabled(this.categoriesPanel, true);
    this.setAttribute('aria-expanded', 'false');
    this.addEventListener('click', this.onToggle);
    this.addEventListener('focusout', this.onFocusOut);
    this.categoriesPanel.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onToggle);
    this.removeEventListener('focusout', this.onFocusOut);
    this.categoriesPanel?.removeEventListener('focusout', this.onFocusOut);
    document.removeEventListener('click', this.onDocClick);
  }

  onToggle(event) {
    event.stopPropagation();
    this._isOpen ? this.close() : this.open();
  }

  onDocClick(event) {
    if (this.contains(event.target)) return;
    if (this.categoriesPanel?.contains(event.target)) return;
    this.close();
  }

  onFocusOut(event) {
    const next = event.relatedTarget;
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (this.contains(active) || this.categoriesPanel?.contains(active)) return;
      if (next && (this.contains(next) || this.categoriesPanel?.contains(next))) return;
      this.close();
    });
  }

  open() {
    if (!this.categoriesPanel || this._isOpen) return;
    this._isOpen = true;
    setInteractionDisabled(this.categoriesPanel, false);
    this.categoriesPanel.classList.add('active');
    this.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      if (!this._isOpen) return;
      document.addEventListener('click', this.onDocClick);
    });
  }

  close() {
    if (!this.categoriesPanel || !this._isOpen) return;
    this._isOpen = false;
    setInteractionDisabled(this.categoriesPanel, true);
    this.categoriesPanel.classList.remove('active');
    this.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocClick);
  }
}
customElements.define('categories-toggle', CategoriesToggle, { extends: 'button' });
